import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Loader2, Plus, Trash2, Upload, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const Card = ({ title, children }) => (
  <div className="bg-[#1B3352] border border-white/10 rounded-2xl p-8 mb-6">
    <h2 className="text-white text-lg font-medium mb-5">{title}</h2>
    {children}
  </div>
);

const SaveBtn = ({ loading, testId, children = "Save" }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testId}
    className="rounded-full bg-[#5ECCA0] text-[#14263D] font-medium px-6 py-2.5 disabled:opacity-60"
  >
    {loading ? "Saving..." : children}
  </button>
);

const ContactForm = ({ token }) => {
  const [info, setInfo] = useState({ address: "", phone: "", email: "", hours: "" });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    axios.get(`${API}/contact-info`).then((res) => {
      setInfo(res.data);
      setFetching(false);
    }).catch(() => setFetching(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/contact-info`, info, authHeader(token));
      toast.success("Contact info updated");
    } catch (err) {
      toast.error("Failed to update — check you're still logged in");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return null;

  return (
    <Card title="Contact Information">
      <form onSubmit={save}>
        <div className="grid sm:grid-cols-2 gap-5 mb-5">
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Phone</Label>
            <Input value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })}
              className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="admin-phone-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Email</Label>
            <Input value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })}
              className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="admin-email-input" />
          </div>
        </div>
        <div className="mb-5">
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Address</Label>
          <Textarea value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} rows={2}
            className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="admin-address-input" />
        </div>
        <div className="mb-6">
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">OPD Hours</Label>
          <Input value={info.hours} onChange={(e) => setInfo({ ...info, hours: e.target.value })}
            className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="admin-hours-input" />
        </div>
        <SaveBtn loading={loading} testId="admin-contact-save-btn">Save Contact Info</SaveBtn>
      </form>
    </Card>
  );
};

const ChangePasswordForm = ({ token }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/admin/change-password`,
        { current_password: current, new_password: next }, authHeader(token));
      toast.success("Password changed");
      setCurrent(""); setNext("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Change Password">
      <form onSubmit={save}>
        <div className="grid sm:grid-cols-2 gap-5 mb-6">
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Current Password</Label>
            <div className="relative mt-2">
              <Input type={showCurrent ? "text" : "password"} value={current} onChange={(e) => setCurrent(e.target.value)}
                className="bg-[#14263D] border-white/15 text-white pr-10" data-testid="admin-current-password-input" />
              <button type="button" onClick={() => setShowCurrent((v) => !v)} tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
                data-testid="admin-current-password-toggle-btn">
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">New Password</Label>
            <div className="relative mt-2">
              <Input type={showNext ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)}
                className="bg-[#14263D] border-white/15 text-white pr-10" data-testid="admin-new-password-input" />
              <button type="button" onClick={() => setShowNext((v) => !v)} tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
                data-testid="admin-new-password-toggle-btn">
                {showNext ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </div>
        <SaveBtn loading={loading} testId="admin-password-save-btn">Change Password</SaveBtn>
      </form>
    </Card>
  );
};

const SiteContentForm = ({ token }) => {
  const [stats, setStats] = useState([]);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    axios.get(`${API}/site-content`).then((res) => {
      setStats(res.data.trust_stats?.length ? res.data.trust_stats : [
        { k: "15+ YEARS", v: "In active practice" },
        { k: "MCh", v: "Neurosurgery" },
        { k: "FELLOWSHIP", v: "Minimally Invasive Spine" },
        { k: "BHUBANESWAR", v: "Odisha, India" },
      ]);
      setWords(res.data.marquee_words?.length ? res.data.marquee_words : [
        "Clear explanations", "Honest options", "Minimally invasive", "Patient-first", "Brain & spine",
      ]);
      setFetching(false);
    }).catch(() => setFetching(false));
  }, []);

  const updateStat = (i, field, value) => {
    const copy = [...stats];
    copy[i] = { ...copy[i], [field]: value };
    setStats(copy);
  };

  const updateWord = (i, value) => {
    const copy = [...words];
    copy[i] = value;
    setWords(copy);
  };

  const addWord = () => setWords([...words, ""]);
  const removeWord = (i) => setWords(words.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/site-content`,
        { trust_stats: stats, marquee_words: words.filter((w) => w.trim()) }, authHeader(token));
      toast.success("Site content updated");
    } catch (err) {
      toast.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return null;

  return (
    <Card title="Trust Strip &amp; Marquee">
      <form onSubmit={save}>
        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui block mb-3">Trust Strip Stats</Label>
        {stats.map((s, i) => (
          <div key={i} className="grid grid-cols-2 gap-3 mb-3">
            <Input value={s.k} onChange={(e) => updateStat(i, "k", e.target.value)} placeholder="Label"
              className="bg-[#14263D] border-white/15 text-white" data-testid={`admin-stat-k-${i}`} />
            <Input value={s.v} onChange={(e) => updateStat(i, "v", e.target.value)} placeholder="Detail"
              className="bg-[#14263D] border-white/15 text-white" data-testid={`admin-stat-v-${i}`} />
          </div>
        ))}

        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui block mt-7 mb-3">Marquee Ticker Words</Label>
        {words.map((w, i) => (
          <div key={i} className="flex gap-3 mb-3">
            <Input value={w} onChange={(e) => updateWord(i, e.target.value)}
              className="bg-[#14263D] border-white/15 text-white" data-testid={`admin-word-${i}`} />
            <button type="button" onClick={() => removeWord(i)} className="text-white/40 hover:text-red-400 px-2">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button type="button" onClick={addWord} className="text-[#5ECCA0] text-sm flex items-center gap-1 mb-6">
          <Plus size={14} /> Add word
        </button>

        <div>
          <SaveBtn loading={loading} testId="admin-sitecontent-save-btn">Save Changes</SaveBtn>
        </div>
      </form>
    </Card>
  );
};

const GalleryForm = ({ token }) => {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadItems = () => {
    axios.get(`${API}/gallery`).then((res) => setItems(res.data)).catch(() => {});
  };

  useEffect(() => { loadItems(); }, []);

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!preview || !label.trim()) {
      toast.error("Add a label and choose a photo first");
      return;
    }
    setUploading(true);
    try {
      await axios.post(`${API}/gallery`,
        { label, image_base64: preview, order: items.length }, authHeader(token));
      toast.success("Photo uploaded");
      setLabel(""); setPreview(null);
      loadItems();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`, authHeader(token));
      toast.success("Removed");
      loadItems();
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  return (
    <Card title="Gallery Photos">
      <form onSubmit={upload} className="mb-8 pb-8 border-b border-white/10">
        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Label</Label>
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Clinic exterior"
          className="mt-2 mb-4 bg-[#14263D] border-white/15 text-white" data-testid="admin-gallery-label-input" />

        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Photo</Label>
        <input type="file" accept="image/*" onChange={onFileChange}
          className="mt-2 mb-4 block text-white/70 text-sm" data-testid="admin-gallery-file-input" />

        {preview && (
          <img src={preview} alt="Preview" className="w-32 h-32 object-cover rounded-lg mb-4 border border-white/15" />
        )}

        <button type="submit" disabled={uploading} data-testid="admin-gallery-upload-btn"
          className="rounded-full bg-[#5ECCA0] text-[#14263D] font-medium px-6 py-2.5 disabled:opacity-60 flex items-center gap-2">
          <Upload size={15} /> {uploading ? "Uploading..." : "Upload Photo"}
        </button>
      </form>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {items.map((item) => (
          <div key={item.id} className="relative group">
            <img src={item.image_base64} alt={item.label} className="w-full aspect-square object-cover rounded-lg border border-white/15" />
            <div className="text-white/60 text-xs mt-1.5 truncate">{item.label}</div>
            <button onClick={() => remove(item.id)} data-testid={`admin-gallery-delete-${item.id}`}
              className="absolute top-2 right-2 bg-black/60 text-white/80 hover:text-red-400 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-white/40 text-sm col-span-full">No photos uploaded yet.</div>
        )}
      </div>
    </Card>
  );
};

const VideoForm = ({ token }) => {
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const loadItems = () => {
    axios.get(`${API}/videos`).then((res) => setItems(res.data)).catch(() => {});
  };

  useEffect(() => { loadItems(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!caption.trim() || !url.trim()) {
      toast.error("Add a caption and a YouTube URL first");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/videos`,
        { caption, youtube_url: url, order: items.length }, authHeader(token));
      toast.success("Video added");
      setCaption(""); setUrl("");
      loadItems();
    } catch (err) {
      toast.error("Failed to add video");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/videos/${id}`, authHeader(token));
      toast.success("Removed");
      loadItems();
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  return (
    <Card title="Video Library">
      <form onSubmit={add} className="mb-8 pb-8 border-b border-white/10">
        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Caption</Label>
        <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Myth-busting: spine surgery recovery"
          className="mt-2 mb-4 bg-[#14263D] border-white/15 text-white" data-testid="admin-video-caption-input" />

        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">YouTube URL</Label>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://youtube.com/shorts/..."
          className="mt-2 mb-4 bg-[#14263D] border-white/15 text-white" data-testid="admin-video-url-input" />

        <button type="submit" disabled={adding} data-testid="admin-video-add-btn"
          className="rounded-full bg-[#5ECCA0] text-[#14263D] font-medium px-6 py-2.5 disabled:opacity-60 flex items-center gap-2">
          <Plus size={15} /> {adding ? "Adding..." : "Add Video"}
        </button>
      </form>

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between bg-[#14263D] border border-white/10 rounded-lg px-4 py-3">
            <div>
              <div className="text-white text-sm">{item.caption}</div>
              <div className="text-white/40 text-xs font-mono-ui mt-0.5 truncate max-w-md">{item.youtube_url}</div>
            </div>
            <button onClick={() => remove(item.id)} data-testid={`admin-video-delete-${item.id}`}
              className="text-white/40 hover:text-red-400 px-2 flex-shrink-0">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-white/40 text-sm">No videos added yet.</div>
        )}
      </div>
    </Card>
  );
};

const AboutTextForm = ({ token }) => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${API}/about-content`).then((res) => setText(res.data?.text || "")).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/about-content`, { text }, authHeader(token));
      toast.success("About section updated");
    } catch (err) {
      toast.error("Failed to save About text");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="About Section Text">
      <form onSubmit={save}>
        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">About Text</Label>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8}
          placeholder="Write the About section text. Leave a blank line between paragraphs."
          className="mt-2 mb-4 w-full rounded-lg bg-[#14263D] border border-white/15 text-white p-3 text-sm"
          data-testid="admin-about-text-input" />

        <button type="submit" disabled={saving} data-testid="admin-about-save-btn"
          className="rounded-full bg-[#5ECCA0] text-[#14263D] font-medium px-6 py-2.5 disabled:opacity-60 flex items-center gap-2">
          {saving ? "Saving..." : "Save About Text"}
        </button>
      </form>
    </Card>
  );
};

const HospitalForm = ({ token }) => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [years, setYears] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [adding, setAdding] = useState(false);

  const loadItems = () => {
    axios.get(`${API}/hospitals`).then((res) => setItems(res.data)).catch(() => {});
  };

  useEffect(() => { loadItems(); }, []);

  const onLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Add a hospital name first");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/hospitals`, {
        name, role, years, logo_base64: logoPreview || "", order: items.length,
      }, authHeader(token));
      toast.success("Hospital added");
      setName(""); setRole(""); setYears(""); setLogoPreview(null);
      loadItems();
    } catch (err) {
      toast.error("Failed to add hospital");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/hospitals/${id}`, authHeader(token));
      toast.success("Removed");
      loadItems();
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  return (
    <Card title="Hospital Affiliations">
      <form onSubmit={add} className="mb-8 pb-8 border-b border-white/10">
        <div className="grid sm:grid-cols-2 gap-5 mb-4">
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Hospital Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Apollo Hospitals"
              className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="admin-hospital-name-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Role (optional)</Label>
            <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Senior Consultant"
              className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="admin-hospital-role-input" />
          </div>
        </div>

        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Years (optional)</Label>
        <Input value={years} onChange={(e) => setYears(e.target.value)} placeholder="e.g. 2015–2020"
          className="mt-2 mb-4 bg-[#14263D] border-white/15 text-white" data-testid="admin-hospital-years-input" />

        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Logo (optional)</Label>
        <input type="file" accept="image/*" onChange={onLogoChange}
          className="mt-2 mb-4 block text-white/70 text-sm" data-testid="admin-hospital-logo-input" />

        {logoPreview && (
          <img src={logoPreview} alt="Logo preview" className="w-16 h-16 object-cover rounded-lg mb-4 border border-white/15" />
        )}

        <button type="submit" disabled={adding} data-testid="admin-hospital-add-btn"
          className="rounded-full bg-[#5ECCA0] text-[#14263D] font-medium px-6 py-2.5 disabled:opacity-60 flex items-center gap-2">
          <Plus size={15} /> {adding ? "Adding..." : "Add Hospital"}
        </button>
      </form>

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between bg-[#14263D] border border-white/10 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              {item.logo_base64 && (
                <img src={item.logo_base64} alt={item.name} className="w-10 h-10 rounded-lg object-cover" />
              )}
              <div>
                <div className="text-white text-sm">{item.name}</div>
                {(item.role || item.years) && (
                  <div className="text-white/40 text-xs mt-0.5">
                    {item.role}{item.role && item.years ? " · " : ""}{item.years}
                  </div>
                )}
              </div>
            </div>
            <button onClick={() => remove(item.id)} data-testid={`admin-hospital-delete-${item.id}`}
              className="text-white/40 hover:text-red-400 px-2 flex-shrink-0">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-white/40 text-sm">No hospitals added yet.</div>
        )}
      </div>
    </Card>
  );
};

const DoctorPhotoForm = ({ token }) => {
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    axios.get(`${API}/doctor-photo`).then((res) => {
      setPreview(res.data.image_base64 || null);
      setFetching(false);
    }).catch(() => setFetching(false));
  }, []);

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!preview) {
      toast.error("Choose a photo first");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/doctor-photo`, { image_base64: preview }, authHeader(token));
      toast.success("Photo updated — appears on Hero and About sections");
    } catch (err) {
      toast.error("Failed to update photo");
    } finally {
      setSaving(false);
    }
  };

  if (fetching) return null;

  return (
    <Card title="Doctor's Photo">
      <p className="text-white/50 text-sm mb-5">Used in both the Hero section and the About section.</p>
      <form onSubmit={save}>
        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Photo</Label>
        <input type="file" accept="image/*" onChange={onFileChange}
          className="mt-2 mb-4 block text-white/70 text-sm" data-testid="admin-doctor-photo-input" />

        {preview && (
          <img src={preview} alt="Preview" className="w-32 aspect-[4/5] object-cover rounded-lg mb-4 border border-white/15" />
        )}

        <SaveBtn loading={saving} testId="admin-doctor-photo-save-btn">Save Photo</SaveBtn>
      </form>
    </Card>
  );
};

const AppointmentsList = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/appointments`, authHeader(token)).then((res) => {
      setItems(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token]);

  if (loading) return null;

  return (
    <Card title="Appointment Requests">
      <p className="text-white/50 text-sm mb-5">
        Every submission also triggers an email — this list is a backup in case anything is missed.
      </p>
      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="bg-[#14263D] border border-white/10 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-white font-medium">{a.name}</div>
                <div className="text-white/60 text-sm mt-0.5">{a.phone}</div>
              </div>
              <div className="text-white/40 text-xs font-mono-ui whitespace-nowrap">
                {new Date(a.created_at).toLocaleString()}
              </div>
            </div>
            <div className="text-white/70 text-sm mt-3">{a.reason}</div>
            {a.note && <div className="text-white/50 text-xs mt-2">{a.note}</div>}
            {a.document_base64 && (
              <a href={a.document_base64} download={`${a.name}-attachment`}
                className="inline-flex items-center gap-1.5 text-[#5ECCA0] text-xs mt-3 hover:underline">
                📎 View attached document
              </a>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-white/40 text-sm">No appointment requests yet.</div>
        )}
      </div>
    </Card>
  );
};

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const LocationsForm = ({ token }) => {
  const [items, setItems] = useState([]);
  const [city, setCity] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [address, setAddress] = useState("");
  const [days, setDays] = useState([]);
  const [timeRange, setTimeRange] = useState("");
  const [adding, setAdding] = useState(false);

  const loadItems = () => {
    axios.get(`${API}/clinic-locations`).then((res) => setItems(res.data)).catch(() => {});
  };

  useEffect(() => { loadItems(); }, []);

  const toggleDay = (d) => {
    setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);
  };

  const add = async (e) => {
    e.preventDefault();
    if (!city.trim() || !clinicName.trim()) {
      toast.error("Add a city and clinic name first");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/clinic-locations`, {
        city, clinic_name: clinicName, address, available_days: days, time_range: timeRange, order: items.length,
      }, authHeader(token));
      toast.success("Location added");
      setCity(""); setClinicName(""); setAddress(""); setDays([]); setTimeRange("");
      loadItems();
    } catch (err) {
      toast.error("Failed to add location");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/clinic-locations/${id}`, authHeader(token));
      toast.success("Removed");
      loadItems();
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  return (
    <Card title="Clinic Locations">
      <form onSubmit={add} className="mb-8 pb-8 border-b border-white/10">
        <div className="grid sm:grid-cols-2 gap-5 mb-4">
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">City</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Bhubaneswar"
              className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="admin-location-city-input" />
          </div>
          <div>
            <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Clinic Name</Label>
            <Input value={clinicName} onChange={(e) => setClinicName(e.target.value)} placeholder="e.g. Life & Mind Clinic"
              className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="admin-location-name-input" />
          </div>
        </div>

        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Address</Label>
        <Textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} placeholder="Full address"
          className="mt-2 mb-4 bg-[#14263D] border-white/15 text-white" data-testid="admin-location-address-input" />

        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui block mb-2">Available Days</Label>
        <div className="flex flex-wrap gap-2 mb-4">
          {DAYS.map((d) => (
            <button key={d} type="button" onClick={() => toggleDay(d)}
              data-testid={`admin-location-day-${d}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                days.includes(d) ? "bg-[#5ECCA0] text-[#14263D] border-[#5ECCA0]" : "bg-[#14263D] text-white/60 border-white/15"
              }`}>
              {d}
            </button>
          ))}
        </div>

        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Time Range</Label>
        <Input value={timeRange} onChange={(e) => setTimeRange(e.target.value)} placeholder="e.g. 10 AM - 2 PM"
          className="mt-2 mb-4 bg-[#14263D] border-white/15 text-white" data-testid="admin-location-time-input" />

        <button type="submit" disabled={adding} data-testid="admin-location-add-btn"
          className="rounded-full bg-[#5ECCA0] text-[#14263D] font-medium px-6 py-2.5 disabled:opacity-60 flex items-center gap-2">
          <Plus size={15} /> {adding ? "Adding..." : "Add Location"}
        </button>
      </form>

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between bg-[#14263D] border border-white/10 rounded-lg px-4 py-3">
            <div>
              <div className="text-white text-sm">{item.clinic_name} — {item.city}</div>
              <div className="text-white/40 text-xs mt-0.5">{item.address}</div>
              <div className="text-white/40 text-xs mt-0.5">
                {item.available_days?.join(", ")} {item.time_range && `· ${item.time_range}`}
              </div>
            </div>
            <button onClick={() => remove(item.id)} data-testid={`admin-location-delete-${item.id}`}
              className="text-white/40 hover:text-red-400 px-2 flex-shrink-0">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-white/40 text-sm">No locations added yet.</div>
        )}
      </div>
    </Card>
  );
};

const ResetRolePasswordForm = ({ token, role, label, endpoint, testPrefix }) => {
  const [newPassword, setNewPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!newPassword.trim()) { toast.error("Enter a new password"); return; }
    setLoading(true);
    try {
      await axios.put(`${API}${endpoint}`, { new_password: newPassword }, authHeader(token));
      toast.success(`${label} password updated`);
      setNewPassword("");
    } catch (err) {
      toast.error(`Failed to update ${label} password`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title={`${label} Password`}>
      <form onSubmit={save}>
        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">New Password</Label>
        <div className="relative mt-2 mb-4 max-w-sm">
          <Input type={show ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            className="bg-[#14263D] border-white/15 text-white pr-10" data-testid={`admin-${testPrefix}-new-password-input`} />
          <button type="button" onClick={() => setShow((v) => !v)} tabIndex={-1}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
            data-testid={`admin-${testPrefix}-password-toggle-btn`}>
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <SaveBtn loading={loading} testId={`admin-${testPrefix}-save-btn`}>{`Update ${label} Password`}</SaveBtn>
      </form>
    </Card>
  );
};

const PROCEDURE_OPTIONS = [
  { slug: "tlif", label: "TLIF" },
  { slug: "open-tlif", label: "Open TLIF" },
  { slug: "mis-tlif", label: "MIS TLIF" },
  { slug: "endo-tlif", label: "Endo TLIF" },
  { slug: "endoscopic-discectomy", label: "Endoscopic Discectomy" },
  { slug: "endoscopic-carpal-tunnel", label: "Endoscopic Carpal Tunnel" },
  // Add more here once approved: acdf, lateral-mass-fixation, brain-tumor-surgery, general-neurosurgery
];

const SpecializationFAQForm = ({ token }) => {
  const [slug, setSlug] = useState(PROCEDURE_OPTIONS[0].slug);
  const [items, setItems] = useState([]);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadItems = () => {
    axios.get(`${API}/specialization-faqs`, { params: { slug } }).then((res) => setItems(res.data)).catch(() => {});
  };
  useEffect(() => { loadItems(); }, [slug]); // eslint-disable-line

  const resetForm = () => { setQuestion(""); setAnswer(""); setEditingId(null); };

  const save = async (e) => {
    e.preventDefault();
    if (!question.trim() || !answer.trim()) { toast.error("Add both a question and an answer"); return; }
    setSaving(true);
    try {
      if (editingId) {
        const existing = items.find((i) => i.id === editingId);
        await axios.put(`${API}/specialization-faqs/${editingId}`, {
          id: editingId, specialization_slug: slug, question, answer, order: existing ? existing.order : 0,
        }, authHeader(token));
        toast.success("FAQ updated");
      } else {
        await axios.post(`${API}/specialization-faqs`, {
          specialization_slug: slug, question, answer, order: items.length,
        }, authHeader(token));
        toast.success("FAQ added");
      }
      resetForm();
      loadItems();
    } catch (err) {
      toast.error("Failed to save FAQ");
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setQuestion(item.question);
    setAnswer(item.answer);
    setEditingId(item.id);
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/specialization-faqs/${id}`, authHeader(token));
      toast.success("Removed");
      if (editingId === id) resetForm();
      loadItems();
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  return (
    <Card title="Specialization FAQs">
      <div className="mb-6">
        <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Procedure</Label>
        <select value={slug} onChange={(e) => { setSlug(e.target.value); resetForm(); }} data-testid="admin-faq-procedure-select"
          className="mt-2 w-full max-w-sm rounded-lg bg-[#14263D] border border-white/15 text-white p-2.5 text-sm">
          {PROCEDURE_OPTIONS.map((p) => <option key={p.slug} value={p.slug}>{p.label}</option>)}
        </select>
      </div>

      <form onSubmit={save} className="mb-8 pb-8 border-b border-white/10">
        <div className="mb-4">
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Question</Label>
          <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. Will I need physiotherapy?"
            className="mt-2 bg-[#14263D] border-white/15 text-white" data-testid="admin-faq-question-input" />
        </div>
        <div className="mb-4">
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Answer</Label>
          <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={4}
            className="mt-2 w-full rounded-lg bg-[#14263D] border border-white/15 text-white p-3 text-sm"
            data-testid="admin-faq-answer-input" />
        </div>
        <div className="flex items-center gap-3">
          <SaveBtn loading={saving} testId="admin-faq-save-btn">{editingId ? "Update FAQ" : "Add FAQ"}</SaveBtn>
          {editingId && (
            <button type="button" onClick={resetForm} className="text-white/50 hover:text-white text-sm">Cancel edit</button>
          )}
        </div>
      </form>

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="bg-[#14263D] border border-white/10 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-white font-medium text-sm">{item.question}</div>
                <div className="text-white/60 text-sm mt-1">{item.answer}</div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button onClick={() => edit(item)} className="text-[#5ECCA0] hover:underline text-sm">Edit</button>
                <button onClick={() => remove(item.id)} className="text-red-400 hover:underline text-sm">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-white/40 text-sm">No FAQs added yet for this procedure.</p>}
      </div>
    </Card>
  );
};

const PAGES = [
  { id: "hero", label: "Hero & Trust Strip" },
  { id: "about", label: "About / Hospitals" },
  { id: "gallery", label: "Gallery" },
  { id: "videos", label: "Videos" },
  { id: "contact", label: "Contact" },
  { id: "locations", label: "Locations" },
  { id: "faqs", label: "Specialization FAQs" },
  { id: "appointments", label: "Appointments" },
  { id: "account", label: "Account" },
];

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState("hero");

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      setToken(res.data.token);
      localStorage.setItem("admin_token", res.data.token);
      toast.success("Logged in");
    } catch (err) {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#14263D] flex items-center justify-center p-6">
        <form onSubmit={login} className="bg-[#1B3352] border border-white/10 rounded-2xl p-8 w-full max-w-sm">
          <h1 className="text-white text-xl font-medium mb-6">Admin Login</h1>
          <Label className="text-[#5ECCA0] text-xs uppercase font-mono-ui">Password</Label>
          <div className="relative mt-2 mb-5">
            <Input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#14263D] border-white/15 text-white pr-10"
              data-testid="admin-password-input"
            />
            <button type="button" onClick={() => setShowPassword((v) => !v)} tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
              data-testid="admin-password-toggle-btn">
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full rounded-full bg-[#5ECCA0] text-[#14263D] font-medium py-3 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Log In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#14263D] p-6 md:p-10">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-white text-2xl font-medium">Admin Dashboard</h1>
          <button onClick={logout} data-testid="admin-logout-btn" className="text-white/50 hover:text-white text-sm">
            Log out
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {PAGES.map((p) => (
            <button
              key={p.id}
              onClick={() => setActivePage(p.id)}
              data-testid={`admin-tab-${p.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activePage === p.id
                  ? "bg-[#5ECCA0] text-[#14263D]"
                  : "bg-[#1B3352] text-white/60 hover:text-white border border-white/10"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {activePage === "hero" && (<><DoctorPhotoForm token={token} /><SiteContentForm token={token} /></>)}
        {activePage === "about" && (<><AboutTextForm token={token} /><HospitalForm token={token} /></>)}
        {activePage === "gallery" && <GalleryForm token={token} />}
        {activePage === "videos" && <VideoForm token={token} />}
        {activePage === "contact" && <ContactForm token={token} />}
        {activePage === "locations" && <LocationsForm token={token} />}
        {activePage === "faqs" && <SpecializationFAQForm token={token} />}
        {activePage === "appointments" && <AppointmentsList token={token} />}
        {activePage === "account" && (
          <>
            <ChangePasswordForm token={token} />
            <ResetRolePasswordForm token={token} label="Reception" endpoint="/admin/reset-reception-password" testPrefix="reception" />
            <ResetRolePasswordForm token={token} label="Clinic" endpoint="/admin/reset-clinic-password" testPrefix="clinic" />
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;
